/*!
* gallery-viewer.js v1.0.2
* Author: Oleg Kuznetsov
* Author URL: https://designbyoleg.com
* Description: Gallery viewer plugin for lightbox or embedded use
* License: MIT
*/
(function ($) {
    const defaultOptions = {
        triggerSelector: '.gallery-item a',
        imageSrcAttr: 'href',
        showArrows: true,
        enableSwipe: true,
        enableKeyboard: true,
        showCaptions: true,
        sameGalleryOnly: true,
        transitionDuration: 300, // Fade transition duration in ms
        renderTarget: null, // Optional jQuery object or selector for non-lightbox rendering
        lightboxId: 'gallery-lightbox' // ID for the lightbox instance
    };

    // Store lightbox instance for reuse
    let lightboxInstance = null;

    class GalleryViewer {
        constructor($el, options) {
            this.$el = $el;
            this.options = { ...defaultOptions, ...options };
            this.images = [];
            this.currentIndex = 0;
            this.init();
        }

        init() {
            this.collectImages();
            this.bindEvents();
        }

        collectImages() {
            const $galleries = this.options.sameGalleryOnly ? this.$el : $('.gallery');
            $galleries.each((i, gallery) => {
                $(gallery).find(this.options.triggerSelector).each((j, link) => {
                    const href = $(link).attr(this.options.imageSrcAttr);
                    // Check if href is likely an image (basic check for common image extensions)
                    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(href)) {
                        const caption = $(link).closest('.gallery-item').find('figcaption').text().trim();
                        this.images.push({ src: href, caption });
                    }
                });
            });
        }

        bindEvents() {
            this.$el.on('click.galleryViewer', this.options.triggerSelector, (e) => {
                const $link = $(e.currentTarget);
                const href = $link.attr(this.options.imageSrcAttr);
                // Only handle image links
                if (/\.(jpg|jpeg|png|gif|webp)$/i.test(href)) {
                    e.preventDefault();
                    const index = this.images.findIndex(img => img.src === href);
                    if (index !== -1) {
                        this.openGallery(index);
                    }
                }
                // Non-image links proceed normally
            });
        }

        openGallery(startIndex) {
            this.currentIndex = startIndex;
            const $content = this.buildGalleryContent();

            if (this.options.renderTarget) {
                // Non-lightbox mode: Render in specified target
                const $target = $(this.options.renderTarget);
                $target.empty().append($content);
                this.bindNavigationEvents($content);
            } else {
                // Lightbox mode: Initialize or reuse lightbox and open content
                if (!lightboxInstance) {
                    lightboxInstance = $.lightbox.init({
                        id: this.options.lightboxId,
                        closeButton: true,
                        closeOnBackdrop: true,
                        closeOnEsc: true,
                        closeOnSwipe: this.options.enableSwipe,
                        lockScroll: true,
                        clearOnClose: true
                    });
                }
                lightboxInstance.open($content);
                this.bindNavigationEvents(lightboxInstance.$content);
            }
        }

        buildGalleryContent() {
            const image = this.images[this.currentIndex];
            const $content = $(`
<div class="gallery-viewer">
<div class="gallery-viewer-main">
<img src="${image.src}" alt="Gallery image" style="object-fit: contain; max-width: 100%; max-height: 100%;">
</div>
${this.options.showCaptions ? `<div class="gallery-viewer-caption" style="${image.caption ? '' : 'display: none;'}">${image.caption}</div>` : ''}
${this.options.showArrows ? `
<a role="button" tabindex="0" class="gallery-viewer-prev" aria-label="Previous image">‹</a>
<a role="button" tabindex="0" class="gallery-viewer-next" aria-label="Next image">›</a>
` : ''}
<div class="gallery-viewer-filmstrip" style="display: none;">
<!-- Placeholder for future film strip implementation -->
<!-- Thumbnails will be 60px square, scrollable, with current image highlighted -->
</div>
</div>
`);
            return $content;
        }

        bindNavigationEvents($content) {
            // Arrow button navigation
            if (this.options.showArrows) {
                $content.find('.gallery-viewer-prev').on('click.galleryViewer', () => this.navigate(-1));
                $content.find('.gallery-viewer-next').on('click.galleryViewer', () => this.navigate(1));
            }

            // Keyboard navigation
            if (this.options.enableKeyboard) {
                const $target = this.options.renderTarget ? $(this.options.renderTarget) : $(document);
                $target.off('keydown.galleryViewer').on('keydown.galleryViewer', (e) => {
                    // In non-lightbox mode, only handle keyboard if target is visible
                    if (this.options.renderTarget && !$target.is(':visible')) return;
                    if (e.key === 'ArrowLeft') {
                        this.navigate(-1);
                    } else if (e.key === 'ArrowRight') {
                        this.navigate(1);
                    }
                });
            }

            // Swipe navigation
            if (this.options.enableSwipe) {
                let startX = 0;
                let isEdgeSwipe = false;
                $content.find('.gallery-viewer-main').off('touchstart.galleryViewer').on('touchstart.galleryViewer', (e) => {
                    startX = e.originalEvent.touches[0].clientX;
                    // Detect edge swipe (within 50px of screen edge)
                    isEdgeSwipe = startX < 50 || startX > window.innerWidth - 50;
                });
                $content.find('.gallery-viewer-main').off('touchend.galleryViewer').on('touchend.galleryViewer', (e) => {
                    if (isEdgeSwipe) return; // Ignore edge swipes
                    const endX = e.originalEvent.changedTouches[0].clientX;
                    const deltaX = endX - startX;
                    if (Math.abs(deltaX) > 100) {
                        this.navigate(deltaX > 0 ? -1 : 1);
                    }
                });
            }
        }

        navigate(direction) {
            const newIndex = this.currentIndex + direction;
            if (newIndex < 0 || newIndex >= this.images.length) return; // Boundary check

            const $container = this.options.renderTarget ? $(this.options.renderTarget) : lightboxInstance.$content;
            const $main = $container.find('.gallery-viewer-main');
            const $img = $main.find('img');
            const $caption = $container.find('.gallery-viewer-caption');

            // Fade out
            $main.fadeOut(this.options.transitionDuration / 2, () => {
                this.currentIndex = newIndex;
                const image = this.images[this.currentIndex];
                $img.attr('src', image.src);
                if (this.options.showCaptions && $caption.length) {
                    if (image.caption) {
                        $caption.text(image.caption).show();
                    } else {
                        $caption.hide();
                    }
                }
                // Fade in
                $main.fadeIn(this.options.transitionDuration / 2);
            });
        }
    }

    // jQuery plugin
    $.fn.galleryViewer = function (options = {}) {
        return this.each(function () {
            new GalleryViewer($(this), options);
        });
    };
})(jQuery);